import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import {
  ThumbUpExactIcon,
  CommentExactIcon,
  CoinsStackExactIcon,
} from '@root/shared/ui-kit'

type ProfileRanksProps = {
  coins: number | string
  likes: number | string
  mentions: number | string
  onCoinsClick: () => void
  onLikesClick: () => void
  onMentionsClick: () => void
}

export const ProfileRanks: React.FC<ProfileRanksProps> = ({
  coins,
  likes,
  mentions,
  onLikesClick,
  onMentionsClick,
}) => {
  const { t } = useTranslation()
  return (
    <Root>
      <Count
        onClick={onLikesClick}
        count={likes}
        icon={<ThumbUpExactIcon width={13} height={13} />}
        description={
          typeof likes === 'number'
            ? t('common.likes', {
                count: likes,
              })
            : t('common.likes_other')
        }
      />
      <Count
        // TODO: uncomment when coins are implemented
        // onClick={onCoinsClick}
        count={coins}
        icon={<CoinsStackExactIcon width={13} height={13} />}
        description={
          typeof coins === 'number'
            ? t('common.coins', {
                count: coins,
              })
            : t('common.coins_other')
        }
        css={css`
          cursor: default;
        `}
      />
      <Count
        onClick={onMentionsClick}
        count={mentions}
        icon={<CommentExactIcon width={13} height={13} />}
        description={
          typeof mentions === 'number'
            ? t('common.mentions', {
                count: mentions,
              })
            : t('common.mentions_other')
        }
      />
    </Root>
  )
}

const Count = (props: {
  icon: React.ReactNode
  count: number | string
  description: string
  onClick?: () => void
}) => (
  <CountRoot type="button" onClick={props.onClick} $onClick={props.onClick}>
    <CountIcon>{props.icon}</CountIcon>
    <CountNumber>{props.count}</CountNumber>
    <CountDescription aria-disabled={!props.onClick}>
      {props.description}
    </CountDescription>
  </CountRoot>
)

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
`

const CountRoot = styled.button<{ $onClick?: () => void }>`
  all: unset;
  color: var(--text-9);
  cursor: auto;
  text-align: center;
  display: flex;
  gap: 4px;
  padding: 10px;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease-in-out;

  ${({ $onClick }) =>
    $onClick &&
    css`
      cursor: pointer;

      &:hover {
        color: var(--text-11);
      }
    `}
`
const CountIcon = styled.div`
  font-weight: 700;
  font-size: 14px;
  width: 13px;
  height: 13px;
`
const CountNumber = styled.div`
  font-weight: 600;
  font-size: 13px;
`
const CountDescription = styled.div`
  color: var(--text-6);
  font-weight: 500;
  font-size: 12px;

  transition: color 0.15s ease-in-out;

  ${CountRoot}:hover & {
    &[aria-disabled='false'] {
      color: var(--text-10);
    }
  }
`
